import type {
  ApiError,
  SdkTwoFactorBackupCodesHandle,
  SdkUseTwoFactorOptions
} from '@Pages/pageRuntime/sdk/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  isRestartError,
  navigateAfterSignIn,
  postTwoFactor,
  storeTwoFactorFlow,
  type BackupCodesProps,
  twoFactorFlowUrl,
  useBackupCodesQuery,
  useTwoFactorCompletion,
  useTwoFactorTarget
} from './twoFactorFlows';

type GenerateFields = { code: string } | { recovery_code: string } | Record<string, never>;

export function useTwoFactorBackupCodes(
  options?: SdkUseTwoFactorOptions
): SdkTwoFactorBackupCodesHandle {
  const target = useTwoFactorTarget(options?.resourceId);
  const resourceId = target.resourceId;
  const afterSignIn = useTwoFactorCompletion(resourceId)?.next === 'backup_codes';
  const isActive =
    afterSignIn || (target.signedInResourceId === resourceId && target.twoFactorEnabled);
  const queryClient = useQueryClient();
  const query = useBackupCodesQuery(resourceId, isActive);
  const [codes, setCodes] = useState<string[] | null>(null);

  const generate = useMutation<BackupCodesProps, ApiError, GenerateFields>({
    mutationFn: (fields) => {
      if (resourceId === null) {
        throw new Error('Backup codes need a resource.');
      }

      return postTwoFactor(twoFactorFlowUrl('backupCodes', resourceId), fields);
    },
    onSuccess: (props) => {
      setCodes(props.codes ?? null);

      if (resourceId !== null) {
        storeTwoFactorFlow(queryClient, 'backupCodes', resourceId, props);
      }
    }
  });

  const props = query.data;

  return {
    isActive,
    data: props && {
      remainingCount: props.remainingCount,
      setupAvailable: props.setupAvailable,
      maskedPhone: props.maskedPhone,
      smsConsentRequired: props.smsConsentRequired,
      status: props.status ?? null
    },
    codes,
    isLoading: isActive && query.isLoading,
    error: query.error,
    isPending: generate.isPending,
    actionError: generate.error,
    restartRequired: isRestartError(query.error) || isRestartError(generate.error),
    generate: (factor) =>
      generate
        .mutateAsync(
          factor === undefined
            ? {}
            : 'code' in factor
              ? { code: factor.code }
              : { recovery_code: factor.recoveryCode.trim() }
        )
        .then(
          () => true,
          () => false
        ),
    continue: () => navigateAfterSignIn(props?.continueUrl ?? '/')
  };
}
